/**
 * Loads `/tv/{id}` details for the shows whose bucket depends on them.
 *
 * Only caught-up shows need it: a show with aired episodes left is "Watching"
 * whatever its status. That keeps the first cold run to roughly 145 requests
 * on a 193-show library instead of 193, and to none at all once cached.
 *
 * Failures are silent by design. An unknown status falls back to "ended",
 * which is the same answer the app gave before this existed, so a network
 * problem degrades the Library rather than breaking it.
 */
import { useEffect, useState } from "react";

import type { SimklClient } from "@/api/client";
import type { LibraryShow } from "@/api/schemas";
import { remaining } from "@/lib/episode";
import { parseDetail, readAll, write } from "@/store/details";
import type { ShowDetail } from "@/store/details";

const EMPTY: ReadonlyMap<number, ShowDetail> = new Map();

export function useShowDetails(
  client: SimklClient,
  shows: readonly LibraryShow[],
  enabled: boolean,
): ReadonlyMap<number, ShowDetail> {
  const [statuses, setStatuses] = useState<ReadonlyMap<number, ShowDetail>>(EMPTY);

  // Keyed on the ids that need an answer, so a library refresh that changes
  // nothing relevant does not refetch.
  const needed = shows
    .filter((show) => remaining(show) === 0)
    .map((show) => show.show.ids.simkl)
    .join(",");

  useEffect(() => {
    if (!enabled || needed.length === 0) return;
    const controller = new AbortController();
    const ids = needed.split(",").map(Number);

    void (async () => {
      const cached = await readAll();
      if (controller.signal.aborted) return;
      setStatuses(new Map(cached));

      const missing = ids.filter((id) => !cached.has(id));
      if (missing.length === 0) return;

      const fetched: ShowDetail[] = [];
      for (const simklId of missing) {
        if (controller.signal.aborted) return;
        try {
          fetched.push(parseDetail(simklId, await client.show(simklId, controller.signal)));
        } catch {
          // Leave it uncached so the next visit retries rather than storing
          // a guess with a week-long lifetime.
          continue;
        }
      }
      if (controller.signal.aborted) return;
      await write(fetched);
      setStatuses((current) => {
        const next = new Map(current);
        for (const record of fetched) next.set(record.simklId, record);
        return next;
      });
    })();

    return () => {
      controller.abort();
    };
  }, [client, enabled, needed]);

  return statuses;
}
